// Actionable remediation UI. The Fix card is a native <details> so it's
// accessible and prints expanded; the detail body renders copy-paste DNS
// records and server snippets via the shared CodeBlock (which owns the copy
// button). RemediationPlan groups every fix by effort for the Executive page.

import { Wrench, ChevronRight } from 'lucide-react';
import { CodeBlock } from './Section';
import type { Remediation } from '../types';

const EFFORT_PILL: Record<Remediation['effort'], string> = {
  low: 'pill-ok',
  medium: 'pill-warn',
  high: 'pill-bad'
};

const IMPACT_LABEL: Record<Remediation['impact'], string> = {
  security: 'Security',
  deliverability: 'Email deliverability',
  compliance: 'Compliance',
  brand: 'Brand trust',
  availability: 'Availability'
};

function RemediationDetail({ r }: { r: Remediation }) {
  return (
    <div className="space-y-4">
      {r.steps.length > 0 && (
        <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-ink-2 marker:text-ink-3">
          {r.steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}

      {r.records && r.records.length > 0 && (
        <div className="space-y-2.5">
          {r.records.map((rec, i) => (
            <CodeBlock key={i} label={rec.type === 'file' ? `FILE · ${rec.host}` : `${rec.type} · ${rec.host}`}>
              {rec.value}
            </CodeBlock>
          ))}
        </div>
      )}

      {r.snippets && r.snippets.length > 0 && (
        <div className="space-y-2.5">
          {r.snippets.map((s, i) => (
            <CodeBlock key={i} label={s.label}>
              {s.code}
            </CodeBlock>
          ))}
        </div>
      )}

      {r.refs && r.refs.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="font-semibold uppercase tracking-wider text-ink-3">Reference</span>
          {r.refs.map((ref) => (
            <a
              key={ref.url}
              href={ref.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent"
            >
              {ref.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/** A collapsible fix. Closed by default on screen; forced open when printing. */
export function RemediationCard({ r, defaultOpen = false }: { r: Remediation; defaultOpen?: boolean }) {
  return (
    <details open={defaultOpen} className="group rounded-lg border border-line bg-panel [&[open]>summary_.chev]:rotate-90 print:open">
      <summary className="flex cursor-pointer list-none items-center gap-2.5 p-4 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="chev h-4 w-4 shrink-0 text-ink-3 transition-transform" strokeWidth={2.25} aria-hidden="true" />
        <span className="min-w-0 flex-1 font-semibold text-ink">{r.title}</span>
        <span className={`pill ${EFFORT_PILL[r.effort]}`}>EFFORT: {r.effort.toUpperCase()}</span>
        <span className="pill pill-info hidden sm:inline-flex">{IMPACT_LABEL[r.impact]}</span>
      </summary>
      <div className="border-t border-line px-4 pb-4 pt-4">
        <p className="mb-4 text-sm leading-relaxed text-ink-2">{r.summary}</p>
        <RemediationDetail r={r} />
      </div>
    </details>
  );
}

const EFFORT_ORDER: Record<Remediation['effort'], number> = { low: 0, medium: 1, high: 2 };
const EFFORT_HEADING: Record<Remediation['effort'], string> = {
  low: 'Quick wins',
  medium: 'Moderate effort',
  high: 'Larger projects'
};

/** Every remediation, grouped by effort ascending. Used on the Executive page. */
export function RemediationPlan({ remediations }: { remediations: Remediation[] }) {
  if (remediations.length === 0) return null;
  const groups = (['low', 'medium', 'high'] as const)
    .map((effort) => ({ effort, items: remediations.filter((r) => r.effort === effort) }))
    .filter((g) => g.items.length > 0)
    .sort((a, b) => EFFORT_ORDER[a.effort] - EFFORT_ORDER[b.effort]);

  return (
    <section className="panel-accent-blue p-5 sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <Wrench className="h-4 w-4 text-accent" strokeWidth={2.25} aria-hidden="true" />
        <h2 className="text-base font-semibold text-ink">Remediation plan</h2>
        <span className="pill pill-info ml-1">{remediations.length} fixes</span>
      </div>
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.effort}>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-ink-3">
              {EFFORT_HEADING[g.effort]} · {g.items.length}
            </p>
            <div className="space-y-2.5">
              {g.items.map((r) => (
                <RemediationCard key={r.id} r={r} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Fixes for a single module — for embedding a "How to fix" block on a
 *  report page. Returns null when there's nothing to fix. */
export function ModuleRemediation({
  remediations,
  modules
}: {
  remediations: Remediation[];
  modules: string[];
}) {
  const items = remediations.filter((r) => modules.includes(r.module));
  if (items.length === 0) return null;
  return (
    <div className="space-y-2.5">
      {items.map((r) => (
        <RemediationCard key={r.id} r={r} defaultOpen={items.length === 1} />
      ))}
    </div>
  );
}
